/**
 * IndexedDB-backed outbox for mutations attempted while offline.
 *
 * Design notes:
 *
 *  - Scoped to clock-in/out (the only flows we've promised to work
 *    offline). A generic request queue was tempting, but replaying
 *    arbitrary writes blindly risks double-submits on anything that's
 *    not idempotent. Keeping the queue narrow lets the drain path
 *    encode the right merge semantics per action type.
 *
 *  - The queue is append-only from the UI thread; drain happens when
 *    (a) the `online` event fires, (b) the service worker posts
 *    `peopleos:drain-outbox`, or (c) the page mounts with pending
 *    items. Drain replays in insertion order and deletes on success.
 *
 *  - Drain failures are classified: network errors (offline, 5xx,
 *    fetch throw) leave the item in place; permanent errors (4xx
 *    except 408/429) drop the item and surface a toast — we can't
 *    re-drive a malformed request indefinitely.
 *
 *  - The queue is deliberately *not* keyed by user id. On logout we
 *    call `clearQueue()` so nothing leaks across sessions.
 */

const DB_NAME = 'peopleos-offline';
const DB_VERSION = 1;
const STORE = 'outbox';

export type ClockAction =
  | { kind: 'clock-in'; projectCode?: string; notes?: string }
  | { kind: 'clock-out'; entryId: string };

export interface OutboxItem {
  id?: number;
  queuedAt: number;
  action: ClockAction;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    Promise.resolve(fn(store))
      .then((result) => {
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      })
      .catch(reject);
  });
}

export async function enqueue(action: ClockAction): Promise<void> {
  const item: OutboxItem = { queuedAt: Date.now(), action };
  await withStore('readwrite', (store) => {
    store.add(item);
  });
  // Opportunistically register a background sync so the OS replays us
  // even if the tab gets closed. Falls through silently on browsers
  // that don't support it — the online-event path covers them.
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      // @ts-expect-error — sync is not in the default TS lib
      await reg.sync.register('peopleos-outbox');
    }
  } catch {
    // ignore — drain still fires from online/load handlers
  }
}

export async function listQueued(): Promise<OutboxItem[]> {
  return withStore('readonly', (store) => {
    return new Promise<OutboxItem[]>((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as OutboxItem[]);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function removeItem(id: number): Promise<void> {
  await withStore('readwrite', (store) => {
    store.delete(id);
  });
}

export async function clearQueue(): Promise<void> {
  await withStore('readwrite', (store) => {
    store.clear();
  });
}

export async function count(): Promise<number> {
  return withStore('readonly', (store) => {
    return new Promise<number>((resolve, reject) => {
      const req = store.count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  });
}

/**
 * Drain the outbox by calling the provided replay function for each
 * queued action in FIFO order. Items that replay successfully are
 * removed; items that throw a network-ish error stay in the queue;
 * items that throw with `permanent = true` are dropped.
 */
export async function drain(
  replay: (action: ClockAction) => Promise<void>,
): Promise<{ replayed: number; remaining: number }> {
  const items = await listQueued();
  items.sort((a, b) => a.queuedAt - b.queuedAt);
  let replayed = 0;
  for (const item of items) {
    try {
      await replay(item.action);
      if (item.id != null) await removeItem(item.id);
      replayed += 1;
    } catch (err: unknown) {
      const permanent =
        err instanceof Error && (err as Error & { permanent?: boolean }).permanent;
      if (permanent && item.id != null) {
        await removeItem(item.id);
      } else {
        // stop on first network-ish failure — we're still offline.
        break;
      }
    }
  }
  return { replayed, remaining: (await listQueued()).length };
}
